import { Link, useOutletContext, useParams } from 'react-router-dom';
import { Card, Skeleton } from '../../../components/primitives';
import { ChildSubnav } from '../../../components/parent/ChildSubnav';
import type { LinkedChild } from '../../../lib/queries/parent/useLinkedChildren';
import { useChildHighlights, type ChildHighlight } from '../../../lib/queries/parent/useChildHighlights';
import { useParentConsents } from '../../../lib/queries/parent/useParentConsents';
import { cornerCssVar } from '../../../lib/cornerColors';
import './Highlights.css';

interface OutletShape {
  activeChildId: string | null;
  children: LinkedChild[];
}

const CORNER_LABEL: Record<string, string> = {
  technical_tactical: 'Technical / Tactical',
  physical: 'Physical',
  psychological: 'Psychological',
  social: 'Social',
};

function platformLabel(p: string): string {
  switch (p) {
    case 'YOUTUBE': return 'YouTube';
    case 'VEO':     return 'Veo';
    case 'HUDL':    return 'Hudl';
    case 'INTERNAL':return 'In-app clip';
    default:        return p;
  }
}

function buildExternalUrl(clip: ChildHighlight): string | null {
  if (clip.platform === 'INTERNAL' || !clip.clipUrl) return null;
  // YouTube embed-friendly timestamp: ?t=Ns
  if (clip.platform === 'YOUTUBE') {
    const sep = clip.clipUrl.includes('?') ? '&' : '?';
    return `${clip.clipUrl}${sep}t=${Math.max(0, Math.floor(clip.tsIn))}`;
  }
  return clip.clipUrl;
}

/**
 * `/parent/child/:id/highlights` — corner-tagged highlight clips
 * shared with the parent.
 *
 * Two consent gates layered:
 *   1. Server: RLS on video_tags requires visibility='PARENT' AND
 *      a parent_children link. UI never sees a non-PARENT clip.
 *   2. UI: even when the row is readable, we hide the gallery if the
 *      parent hasn't ticked the MEDIA box. They can fix that in the
 *      consent flow's Settings → Privacy variant (Wave 3.5).
 */
export function ChildHighlights() {
  const { id: routeId } = useParams<{ id: string }>();
  const { children } = useOutletContext<OutletShape>();
  const playerId = routeId ?? null;
  const child = children.find((c) => c.id === playerId) ?? null;

  const consents = useParentConsents();
  const hasMediaConsent = consents.data.isActive('MEDIA');

  const { data: highlights, loading, error } = useChildHighlights(
    hasMediaConsent && playerId ? playerId : null,
  );

  if (!playerId) {
    return (
      <Card variant="opaque">
        <p>Pick a child from the sidebar to see their highlights.</p>
      </Card>
    );
  }

  return (
    <div className="child-highlights">
      <ChildSubnav childId={playerId} />

      <header className="parent-page__head">
        <span className="parent-page__eyebrow">{child?.teamName ?? 'Linked child'}</span>
        <h1 className="parent-page__title">{(child?.name ?? 'Your child').split(' ')[0]}'s highlights</h1>
        <p className="parent-page__lead">
          Short clips your child's coach has saved and flagged for parent
          viewing. Each is corner-tagged so you can see what kind of
          development moment it captures.
        </p>
      </header>

      {consents.loading ? (
        <Card variant="opaque" aria-busy="true">
          <Skeleton variant="text" width="40%" height={16} />
          <Skeleton variant="text" count={2} width="80%" height={14} />
        </Card>
      ) : !hasMediaConsent ? (
        <Card variant="opaque">
          <header className="child-highlights__section-head">
            <span className="parent-page__eyebrow">Video sharing is off</span>
            <h2 className="child-highlights__section-title">Turn on video to see clips</h2>
          </header>
          <p className="parent-page__lead">
            You haven't ticked <strong>Video sharing</strong> in your consents,
            so coach-shared clips don't appear here. You can change this any
            time — it doesn't affect any other consent.
          </p>
          <p className="parent-page__lead">
            <Link to="/parent/settings">Open Privacy &amp; consent →</Link>
          </p>
        </Card>
      ) : loading ? (
        <div className="child-highlights__grid" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <article key={i} className="child-highlights__card">
              <div className="child-highlights__card-head">
                <Skeleton variant="pill" width={8} height={28} />
                <div className="child-highlights__card-meta">
                  <Skeleton variant="pill" width={48} height={20} />
                </div>
              </div>
              <Skeleton variant="text" width="40%" height={12} />
              <Skeleton variant="text" count={2} width="100%" height={14} />
              <div className="child-highlights__card-foot">
                <Skeleton variant="text" width={80} height={12} />
                <Skeleton variant="text" width={60} height={12} />
              </div>
            </article>
          ))}
        </div>
      ) : error ? (
        <Card variant="opaque">
          <p className="parent-page__lead">
            We couldn't load highlights right now. Please try again later.
          </p>
        </Card>
      ) : highlights.length === 0 ? (
        <Card variant="opaque">
          <header className="child-highlights__section-head">
            <span className="parent-page__eyebrow">Nothing yet</span>
            <h2 className="child-highlights__section-title">No clips shared</h2>
          </header>
          <p className="parent-page__lead">
            When your child's coach saves a clip and flags it for parents,
            it'll appear here. There's nothing to share right now — that's
            normal early in a season.
          </p>
        </Card>
      ) : (
        <div className="child-highlights__grid">
          {highlights.map((clip) => {
            const cornerKey = (clip.cornerId ?? '').replace(/^corner-/, '');
            const cornerColor = cornerCssVar(clip.cornerId);
            const cornerName = CORNER_LABEL[cornerKey] ?? null;
            const externalUrl = buildExternalUrl(clip);
            const duration = Math.max(0, Math.round(clip.tsOut - clip.tsIn));
            const dateStr = clip.createdAt
              ? new Date(clip.createdAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : '';
            return (
              <article key={clip.id} className="child-highlights__card">
                <div className="child-highlights__card-head">
                  <span
                    className="child-highlights__corner"
                    style={{ background: cornerColor }}
                    aria-label={cornerName ?? 'Corner'}
                  />
                  <div className="child-highlights__card-meta">
                    <span className="child-highlights__platform">{platformLabel(clip.platform)}</span>
                    {duration > 0 && (
                      <span className="child-highlights__duration">{duration}s</span>
                    )}
                  </div>
                </div>
                {cornerName && (
                  <p className="child-highlights__corner-label">{cornerName}</p>
                )}
                {clip.coachNote ? (
                  <p className="child-highlights__note">{clip.coachNote}</p>
                ) : (
                  <p className="child-highlights__note child-highlights__note--mute">
                    No coach note attached.
                  </p>
                )}
                <div className="child-highlights__card-foot">
                  {dateStr && <span className="child-highlights__date">{dateStr}</span>}
                  {externalUrl ? (
                    <a
                      className="child-highlights__open"
                      href={externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open clip ↗
                    </a>
                  ) : (
                    <span className="child-highlights__open child-highlights__open--mute">
                      Clip not playable from web
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <p className="parent-page__footnote">
        Coach-shared highlights are stored on YouTube, Veo, Hudl, or in 4CM.
        4CM never shares your child's clips with third parties.
      </p>
    </div>
  );
}
